import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import supabase from '../../supabase/supabase';

const { FiInbox, FiRefreshCw, FiTrash2, FiUserX, FiUserCheck, FiEye, FiX, FiUsers } = FiIcons;

function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewCustomer, setViewCustomer] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('customers_1750000002').select('*').order('created_at', { ascending: false });
    setCustomers(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleSuspend = async (customer) => {
    const suspended = !customer.suspended;
    await supabase.from('customers_1750000002').update({ suspended }).eq('id', customer.id);
    setCustomers((prev) => prev.map((c) => c.id === customer.id ? { ...c, suspended } : c));
  };

  const deleteCustomer = async (id) => {
    if (!confirm('Delete this customer permanently?')) return;
    await supabase.from('customers_1750000002').delete().eq('id', id);
    setCustomers((prev) => prev.filter((c) => c.id !== id));
  };

  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone && c.phone.includes(search))
  );

  const activeCount = customers.filter((c) => !c.suspended).length;
  const suspendedCount = customers.filter((c) => c.suspended).length;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div><h1>Customers</h1><p>Manage and track your customer base</p></div>
        <div className="admin-header-actions">
          <input className="admin-search" placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="admin-btn secondary" onClick={load}><SafeIcon icon={FiRefreshCw} /></button>
        </div>
      </div>

      <div className="admin-stats">
        <div className="admin-stat-card">
          <div className="stat-icon" style={{ color: '#0e8f5b', background: '#eaf7f1' }}><SafeIcon icon={FiUserCheck} /></div>
          <div className="stat-info">
            <div className="stat-value">{activeCount}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-icon" style={{ color: '#dc2626', background: '#fee2e2' }}><SafeIcon icon={FiUserX} /></div>
          <div className="stat-info">
            <div className="stat-value">{suspendedCount}</div>
            <div className="stat-label">Suspended</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-icon" style={{ color: '#3b82f6', background: '#eff6ff' }}><SafeIcon icon={FiUsers} /></div>
          <div className="stat-info">
            <div className="stat-value">{customers.length}</div>
            <div className="stat-label">Total</div>
          </div>
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty"><SafeIcon icon={FiInbox} /><p>No customers found</p></div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Customer</th><th>Email</th><th>Phone</th><th>District</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((customer) => (
                  <tr key={customer.id}>
                    <td><strong>{customer.name}</strong></td>
                    <td>{customer.email}</td>
                    <td>{customer.phone || '—'}</td>
                    <td>{customer.district || '—'}</td>
                    <td>
                      <span className={`status-badge ${customer.suspended ? 'cancelled' : 'delivered'}`}>
                        {customer.suspended ? 'Suspended' : 'Active'}
                      </span>
                    </td>
                    <td>{new Date(customer.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-row-actions">
                        <button className="admin-icon-btn" onClick={() => setViewCustomer(customer)} title="View"><SafeIcon icon={FiEye} /></button>
                        <button className="admin-icon-btn" onClick={() => toggleSuspend(customer)} title={customer.suspended ? 'Unsuspend' : 'Suspend'}>
                          <SafeIcon icon={customer.suspended ? FiUserCheck : FiUserX} />
                        </button>
                        <button className="admin-icon-btn danger" onClick={() => deleteCustomer(customer.id)} title="Delete"><SafeIcon icon={FiTrash2} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewCustomer && (
        <div className="admin-modal-overlay" onClick={() => setViewCustomer(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Customer Profile</h2>
              <button className="admin-icon-btn" onClick={() => setViewCustomer(null)}><SafeIcon icon={FiX} /></button>
            </div>
            <div className="admin-modal-body">
              <div className="customer-profile">
                <div className="customer-avatar">{viewCustomer.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}</div>
                <h3>{viewCustomer.name}</h3>
                <span className={`status-badge ${viewCustomer.suspended ? 'cancelled' : 'delivered'}`}>
                  {viewCustomer.suspended ? 'Suspended' : 'Active'}
                </span>
                <div className="customer-details">
                  <div><span>Email</span><strong>{viewCustomer.email}</strong></div>
                  <div><span>Phone</span><strong>{viewCustomer.phone || 'Not provided'}</strong></div>
                  <div><span>Address</span><strong>{viewCustomer.address || 'Not provided'}</strong></div>
                  <div><span>District</span><strong>{viewCustomer.district || 'Not provided'}</strong></div>
                  <div><span>Joined</span><strong>{new Date(viewCustomer.created_at).toLocaleDateString()}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminCustomers;